import { useState } from 'react';
import { formatLocal } from '@cis/shared';
import type { HistoryEntry } from '@cis/api-client';
import { usePeopleNames, useTaskHistory } from '../../api/tasks.ts';
import { ErrorPanel } from '../../ui/error-boundary.tsx';
import { Pager } from '../../ui/pager.tsx';
import { Skeleton } from '../../ui/skeleton.tsx';

const MAX_VALUE_TEXT = 200;
// Bookkeeping fields every snapshot carries; they say nothing to a person reading the history.
const HIDDEN_FIELDS = new Set(['version']);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** A stored value as plain text (never markup): "—" for empty, JSON for lists and objects. */
export function valueText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const text = typeof v === 'string' ? v : JSON.stringify(v);
  return text.length > MAX_VALUE_TEXT ? `${text.slice(0, MAX_VALUE_TEXT)}…` : text;
}

export interface FieldChange {
  readonly field: string;
  readonly before: string;
  readonly after: string;
}

// Fields that hold user ids (task and step assignees, spec J3): shown as names when known.
const PEOPLE_FIELDS = new Set(['assigneeIds']);

/** A list of user ids as names ("Raul Guzman, Carlos Mares"); an unknown id stays "#12". */
export function peopleText(v: unknown, names: ReadonlyMap<number, string>): string {
  if (!Array.isArray(v)) return valueText(v);
  if (v.length === 0) return '—';
  return valueText(
    v
      .map((id: unknown) => (typeof id === 'number' ? (names.get(id) ?? `#${String(id)}`) : id))
      .join(', '),
  );
}

/** Field-by-field before → after of one event (qa Q21: history lists before/after). */
export function fieldChanges(
  before: unknown,
  after: unknown,
  names: ReadonlyMap<number, string> = new Map(),
): FieldChange[] {
  const b = isRecord(before) ? before : {};
  const a = isRecord(after) ? after : {};
  const text = (f: string, v: unknown): string =>
    PEOPLE_FIELDS.has(f) ? peopleText(v, names) : valueText(v);
  const fields = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  return fields
    .filter((f) => !HIDDEN_FIELDS.has(f) && valueText(b[f]) !== valueText(a[f]))
    .map((f) => ({ field: f, before: text(f, b[f]), after: text(f, a[f]) }));
}

/** Whether any entry on the page changed who is assigned, so names are worth reading. */
const changesPeople = (items: readonly HistoryEntry[]): boolean =>
  items.some((e) => fieldChanges(e.before, e.after).some((c) => PEOPLE_FIELDS.has(c.field)));

function subject(e: HistoryEntry): string {
  if (e.entityType !== 'step') return e.action;
  const no = e.entityKey.slice(e.entityKey.lastIndexOf(':') + 1);
  return `${e.action} (step ${no})`;
}

function Entry(props: {
  readonly entry: HistoryEntry;
  readonly names: ReadonlyMap<number, string>;
}): React.JSX.Element {
  const e = props.entry;
  const changes = fieldChanges(e.before, e.after, props.names);
  return (
    <li className="history__entry">
      <p>
        <strong>{subject(e)}</strong> by {e.user?.displayName ?? 'the system'}, {formatLocal(e.at)}
      </p>
      {e.purged ? (
        <p className="muted">The details of this entry were removed by the owner.</p>
      ) : (
        <ul className="plain-list">
          {changes.map((c) => (
            <li key={c.field}>
              {c.field}: <span className="muted">{c.before}</span> → {c.after}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Who changed what and when, newest first (spec F15). */
export function HistoryPanel(props: { readonly taskId: string }): React.JSX.Element {
  const [page, setPage] = useState(1);
  const history = useTaskHistory(props.taskId, page);
  const names = usePeopleNames(changesPeople(history.data?.items ?? []));
  return (
    <section className="card" aria-labelledby="history-title">
      <h2 className="card__title" id="history-title">
        History
      </h2>
      {history.isPending && <Skeleton label="Loading the history…" />}
      {history.isError && (
        <ErrorPanel
          title="The history could not be loaded"
          error={history.error}
          onRetry={() => void history.refetch()}
        />
      )}
      {history.data?.total === 0 && <p className="muted">Nothing has changed on this task yet.</p>}
      {history.data !== undefined && history.data.items.length > 0 && (
        <>
          <ol className="history" aria-label="Changes, newest first">
            {history.data.items.map((e) => (
              <Entry key={e.id} entry={e} names={names} />
            ))}
          </ol>
          <Pager
            page={history.data.page}
            pageSize={history.data.pageSize}
            total={history.data.total}
            label="History"
            onPage={setPage}
          />
        </>
      )}
    </section>
  );
}
